/**
 * Notification Service
 * Handles local notifications and Firebase Web Push token registration for the PWA.
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  getMessaging,
  getToken as getMessagingToken,
  isSupported as isMessagingSupported,
  onMessage,
  type MessagePayload,
  type Messaging,
} from 'firebase/messaging';
import { API_CONFIG } from '../config/api';
import { ENV_CONFIG } from '../config/env';
import { getUser } from './auth';
import { getFingerprint } from '../utils/fingerprint';

export type NotificationPermissionStatus = 'granted' | 'denied' | 'default';

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
  requireInteraction?: boolean;
  silent?: boolean;
  vibrate?: number[];
}

type PushPayloadData = Record<string, string | undefined>;

class NotificationService {
  private static instance: NotificationService;
  private messaging: Messaging | null = null;
  private foregroundListenerReady = false;
  private lastRegisteredToken: string | null = null;

  private constructor() {
    this.initServiceWorker();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Check if notifications are supported
   */
  public isSupported(): boolean {
    return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
  }

  public isPushConfigured(): boolean {
    const missingKeys = this.getMissingPushConfigKeys();

    if (missingKeys.length > 0) {
      console.warn('Firebase Web Push configuration is incomplete. Missing:', missingKeys);
    }

    return missingKeys.length === 0;
  }

  /**
   * Get current permission status
   */
  public getPermissionStatus(): NotificationPermissionStatus {
    if (!this.isSupported()) {
      return 'denied';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission and register the browser for server push.
   */
  public async requestPermission(): Promise<NotificationPermissionStatus> {
    if (!this.isSupported()) {
      console.warn('Notifications are not supported in this browser');
      return 'denied';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission =
        Notification.permission === 'granted'
          ? 'granted'
          : await this.requestBrowserPermission();

      if (permission === 'granted') {
        await this.syncPushRegistration({ force: true });
      }

      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  public async syncPushRegistration(options: { force?: boolean } = {}): Promise<void> {
    if (!this.isSupported() || Notification.permission !== 'granted') return;

    const missingConfigKeys = this.getMissingPushConfigKeys();
    if (missingConfigKeys.length > 0) {
      console.warn('Firebase Web Push configuration is incomplete. Missing:', missingConfigKeys);
      return;
    }

    try {
      const supported = await isMessagingSupported();
      if (!supported) return;

      const registration = await navigator.serviceWorker.ready;
      const messaging = await this.getMessagingInstance();
      if (!messaging) return;

      const token = await getMessagingToken(messaging, {
        vapidKey: ENV_CONFIG.FIREBASE.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (!token || (!options.force && token === this.lastRegisteredToken)) return;

      await this.registerWebToken(token);
      this.lastRegisteredToken = token;
      this.listenForForegroundMessages(messaging);
    } catch (error) {
      console.error('Error registering web push token:', error);
    }
  }

  public async unregisterPushToken(): Promise<void> {
    if (!this.isSupported() || !this.isPushConfigured()) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      const messaging = await this.getMessagingInstance();
      if (!messaging) return;

      const token = this.lastRegisteredToken || await getMessagingToken(messaging, {
        vapidKey: ENV_CONFIG.FIREBASE.vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return;

      await fetch(`${API_CONFIG.BASE_URL}/devices/${encodeURIComponent(token)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      this.lastRegisteredToken = null;
    } catch (error) {
      console.error('Error unregistering web push token:', error);
    }
  }

  /**
   * Send a local notification. Do not use this for donation payment status.
   */
  public async sendNotification(options: NotificationOptions): Promise<void> {
    if (!this.isSupported()) {
      console.warn('Notifications are not supported');
      return;
    }

    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    try {
      await this.showServiceWorkerNotification(options);
    } catch (error) {
      console.error('Error sending notification:', error);
      // Fallback to browser notification if service worker fails
      this.sendBrowserNotification(options);
    }
  }

  /**
   * Send browser notification (fallback)
   */
  private sendBrowserNotification(options: NotificationOptions): void {
    if (Notification.permission === 'granted') {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/images/icon-192x192.png',
        tag: options.tag || 'default',
        data: options.data,
      });

      // Auto close after 5 seconds if not required to interact
      if (!options.requireInteraction) {
        setTimeout(() => notification.close(), 5000);
      }
    }
  }

  /**
   * Initialize service worker for notifications
   */
  private async initServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        await navigator.serviceWorker.ready;
        if (Notification.permission === 'granted') {
          await this.syncPushRegistration();
        }
      } catch (error) {
        console.error('Service worker registration failed:', error);
      }
    }
  }

  private async getMessagingInstance(): Promise<Messaging | null> {
    if (this.messaging) return this.messaging;

    const app = getApps().length > 0 ? getApps()[0] : initializeApp({
      apiKey: ENV_CONFIG.FIREBASE.apiKey,
      authDomain: ENV_CONFIG.FIREBASE.authDomain,
      projectId: ENV_CONFIG.FIREBASE.projectId,
      messagingSenderId: ENV_CONFIG.FIREBASE.messagingSenderId,
      appId: ENV_CONFIG.FIREBASE.appId,
    });

    this.messaging = getMessaging(app);
    return this.messaging;
  }

  private getMissingPushConfigKeys(): string[] {
    const requiredConfig = {
      VITE_FIREBASE_API_KEY: ENV_CONFIG.FIREBASE.apiKey,
      VITE_FIREBASE_PROJECT_ID: ENV_CONFIG.FIREBASE.projectId,
      VITE_FIREBASE_MESSAGING_SENDER_ID: ENV_CONFIG.FIREBASE.messagingSenderId,
      VITE_FIREBASE_APP_ID: ENV_CONFIG.FIREBASE.appId,
      VITE_FIREBASE_VAPID_KEY: ENV_CONFIG.FIREBASE.vapidKey,
    };

    return Object.entries(requiredConfig)
      .filter(([, value]) => !value)
      .map(([key]) => key);
  }

  private async requestBrowserPermission(): Promise<NotificationPermissionStatus> {
    const request = Notification.requestPermission.bind(Notification);

    if (request.length > 0) {
      return new Promise((resolve) => {
        request((permission) => resolve(permission as NotificationPermissionStatus));
      });
    }

    return request() as Promise<NotificationPermissionStatus>;
  }

  private async registerWebToken(token: string): Promise<void> {
    const userId = getUser()?.id;
    const fingerprint = getFingerprint(userId);
    const response = await fetch(`${API_CONFIG.BASE_URL}/devices/register`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'x-fingerprint': fingerprint,
      },
      body: JSON.stringify({
        fcmToken: token,
        platform: 'web',
        fingerprint,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to register web push token: ${response.status}`);
    }
  }

  private listenForForegroundMessages(messaging: Messaging): void {
    if (this.foregroundListenerReady) return;

    onMessage(messaging, (payload) => {
      const options = this.notificationOptionsFromPayload(payload);
      if (!options) return;
      this.showServiceWorkerNotification(options).catch((error) => {
        console.error('Error showing foreground push notification:', error);
      });
    });

    this.foregroundListenerReady = true;
  }

  private notificationOptionsFromPayload(payload: MessagePayload): NotificationOptions | null {
    const data = payload.data as PushPayloadData | undefined;
    const title = payload.notification?.title || data?.title;
    const body = payload.notification?.body || data?.body;

    if (!title || !body) return null;

    return {
      title,
      body,
      icon: payload.notification?.icon || data?.icon || '/images/icon-192x192.png',
      badge: data?.badge || '/images/icon-72x72.png',
      tag: data?.tag || `${data?.type ?? 'push'}:${data?.id ?? Date.now()}`,
      data: {
        ...data,
        url: data?.url || payload.fcmOptions?.link || '/',
      },
      requireInteraction: data?.type === 'payment_status',
      vibrate: [200, 100, 200],
    };
  }

  private async showServiceWorkerNotification(options: NotificationOptions): Promise<void> {
    const registration = await navigator.serviceWorker.ready;

    await registration.showNotification(options.title, {
      body: options.body,
      icon: options.icon || '/images/icon-192x192.png',
      badge: options.badge || '/images/icon-72x72.png',
      tag: options.tag || 'default',
      data: options.data,
      requireInteraction: options.requireInteraction || false,
      silent: options.silent || false,
      vibrate: options.vibrate || [200, 100, 200],
    });
  }

  /**
   * Send program reminder notification
   */
  public async sendProgramReminder(programName: string): Promise<void> {
    await this.sendNotification({
      title: 'Pengingat Program',
      body: `Jangan lupa untuk berdonasi di program ${programName}`,
      tag: 'program-reminder',
      requireInteraction: false,
    });
  }

  /**
   * Send general notification
   */
  public async sendGeneralNotification(title: string, body: string): Promise<void> {
    await this.sendNotification({
      title,
      body,
      tag: 'general',
      requireInteraction: false,
    });
  }

  /**
   * Clear all notifications
   */
  public async clearAllNotifications(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const notifications = await registration.getNotifications();
        notifications.forEach(notification => notification.close());
      } catch (error) {
        console.error('Error clearing notifications:', error);
      }
    }
  }

  /**
   * Clear notifications by tag
   */
  public async clearNotificationsByTag(tag: string): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const notifications = await registration.getNotifications({ tag });
        notifications.forEach(notification => notification.close());
      } catch (error) {
        console.error('Error clearing notifications by tag:', error);
      }
    }
  }
}

export const notificationService = NotificationService.getInstance();
